import { baseApi } from './baseApi';
import { formatApiUrl } from '../../config/api';
import type { Device, PaginatedResponse, ApiResponse } from '../../types/api';

export interface BulkVerifyResult {
  device_id: string;
  success: boolean;
  message?: string;
  error?: string;
  secure_serial?: string;
  secure_code?: string;
}

/**
 * Bulk verify reports per-device failures inside a 200 response, so a caller
 * has to read `results` — the request succeeding says nothing about the device.
 */
export interface BulkVerifyResponse {
  results: BulkVerifyResult[];
  successful: number;
  failed: number;
}

/** POST /devices/bulk/delete — unknown ids come back in `not_found`. */
export interface BulkArchiveResponse {
  archived: string[];
  not_found: string[];
  successful: number;
  failed: number;
}

interface DeviceFilters {
  status?: 'online' | 'offline' | 'unknown';
  /**
   * A single registration status, or a comma-separated list of them — see
   * UNVERIFIED_REGISTRATION_STATUS_PARAM, which the Unverified tab sends.
   * (The old union here listed only pending/verified/rejected, omitting the
   * 'enrolled' status every commissioned device actually has.)
   */
  registration_status?: string;
  groupId?: string;
  excludeGroup?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UpdateDeviceRequest {
  name?: string;
  customer_name?: string;
  location?: string;
  groupId?: string;
  metadata?: Record<string, unknown>;
}

// Transform backend device response to frontend Device interface
const transformDevice = (device: any): Device => {
  return {
    id: device.id,
    deviceId: device.device_id || device.deviceId || '', // Add the actual device_id
    registrationId: device.registration_id || device.registrationId || '',
    name: device.name || device.device_id || 'Unknown Device',
    customerName: device.customer_name || device.customerName,
    status: device.status || 'unknown',
    registrationStatus: device.registration_status || device.registrationStatus,
    lastSeen: device.last_seen || device.lastSeen || '',
    groupId: device.group_id || device.groupId,
    groupName: device.group_name || device.groupName || device.device_group,
    location: device.location,
    macAddress: device.mac_address || device.macAddress,
    secureSerial: device.secure_serial || device.secureSerial,
    secureCode: device.secure_code || device.secureCode,
    tpm: {
      manufacturer: device.tpm_manufacturer || device.tpm_vendor_id || '',
      version: device.tpm_spec_version || device.tpm_version || '',
      ekPublic: device.ek_public_pem || device.ek_public,
      srkPublic: device.ak_public_pem || device.srk_public,
    },
    hardware: {
      model: device.hardware_model || device.hardware?.model,
      firmware: device.firmware_version || device.hardware?.firmware,
      serialNumber: device.serial_number || device.hardware?.serialNumber,
    },
    clientVersion: device.client_version || device.clientVersion,
    osVersion: device.os_version || device.osVersion,
    comnovoGatewayVersion: device.comnovo_gateway_version || device.comnovoGatewayVersion,
    comnovoVersionUpdatedAt: device.comnovo_version_updated_at || device.comnovoVersionUpdatedAt,
    dotnetRuntimeVersion: device.dotnet_runtime_version ?? device.dotnetRuntimeVersion,
    rebootRequired: device.reboot_required ?? device.rebootRequired,
    osUpdatesPending: device.os_updates_pending ?? device.osUpdatesPending,
    osUpdatesCheckedAt: device.os_updates_checked_at ?? device.osUpdatesCheckedAt,
    // Azure DPS enrollment fields
    dpsIdScope: device.dps_id_scope || device.dpsIdScope,
    dpsEnvironment: device.dps_environment || device.dpsEnvironment,
    dpsEnvironmentUpdatedAt: device.dps_environment_updated_at || device.dpsEnvironmentUpdatedAt,
    dpsEnrollmentStatus: device.dps_enrollment_status || device.dpsEnrollmentStatus,
    dpsAssignedHub: device.dps_assigned_hub || device.dpsAssignedHub,
    dpsEnrolledAt: device.dps_enrolled_at || device.dpsEnrolledAt,
    dpsLastError: device.dps_last_error || device.dpsLastError,
    gsmDisabled: device.gsm_disabled !== undefined ? device.gsm_disabled : device.gsmDisabled,
    gsmStatusUpdatedAt: device.gsm_status_updated_at || device.gsmStatusUpdatedAt,
    modemImei: device.modem_imei || device.modemImei,
    modemIccid: device.modem_iccid || device.modemIccid,
    // ?? not ||: "" (no fixed APN) is a value
    lteApn: device.lte_apn ?? device.lteApn,
    lteApnUpdatedAt: device.lte_apn_updated_at ?? device.lteApnUpdatedAt,
    network: {
      ipAddress: device.ip_address || device.network?.ipAddress,
      connectionType: device.connection_type || device.network?.connectionType,
      macAddress: device.mac_address || device.network?.macAddress,
    },
    ssh: {
      tunnelPort: device.ssh_tunnel_port,
      publicKey: device.ssh_public_key,
      tunnelActive: device.ssh_tunnel_active,
    },
    metadata: device.device_metadata || device.metadata,
    // System metrics
    diskUsage: device.disk_usage !== undefined ? device.disk_usage : device.diskUsage,
    cpuUsage: device.cpu_usage !== undefined ? device.cpu_usage : device.cpuUsage,
    memoryUsage: device.memory_usage !== undefined ? device.memory_usage : device.memoryUsage,
    totalDiskGb: device.total_disk_gb !== undefined ? device.total_disk_gb : device.totalDiskGb,
    createdAt: device.created_at || device.createdAt || '',
    updatedAt: device.updated_at || device.updatedAt || '',
    lastLabelGeneratedAt: device.last_label_generated_at ?? device.lastLabelGeneratedAt ?? null,
  };
};

export interface DeviceStatusHistoryEntry {
  timestamp: string;
  status: string;
  previous_status: string | null;
  duration_seconds: number | null;
  recovered_at: string | null;
  reconnect_count: number;
}

export interface DeviceStatusHistory {
  device_id: string;
  history: DeviceStatusHistoryEntry[];
  total_entries: number;
}

export const deviceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get paginated list of devices
    getDevices: builder.query<PaginatedResponse<Device>, DeviceFilters>({
      query: (filters) => {
        // Build query params, filtering out undefined values
        const params: Record<string, any> = {};
        if (filters.page !== undefined) params.page = filters.page;
        if (filters.pageSize !== undefined) params.page_size = filters.pageSize;
        if (filters.search !== undefined && filters.search !== '') params.search = filters.search;
        if (filters.sortBy !== undefined) params.sort_by = filters.sortBy;
        if (filters.sortOrder !== undefined) params.sort_order = filters.sortOrder;
        if (filters.registration_status !== undefined) params.registration_status = filters.registration_status;
        if (filters.groupId !== undefined) params.groupId = filters.groupId;

        return {
          url: formatApiUrl('devices'),
          params,
        };
      },
      transformResponse: (response: unknown) => {
        // Ensure response has expected structure
        if (!response || typeof response !== 'object') {
          return {
            data: [],
            total: 0,
            page: 1,
            pageSize: 10,
            totalPages: 0,
          };
        }

        // Handle both direct array response and paginated response
        if (Array.isArray(response)) {
          return {
            data: response.map(transformDevice),
            total: response.length,
            page: 1,
            pageSize: response.length,
            totalPages: 1,
          };
        }

        // Ensure data property exists and is an array
        const resp = response as any;
        return {
          data: Array.isArray(resp.data) ? resp.data.map(transformDevice) : [],
          total: resp.total || 0,
          page: resp.page || 1,
          pageSize: resp.pageSize || resp.page_size || resp.limit || 10,
          totalPages: resp.totalPages || resp.total_pages || 0,
        };
      },
      providesTags: (result) =>
        result && result.data && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Device' as const, id })),
              { type: 'Device', id: 'LIST' },
            ]
          : [{ type: 'Device', id: 'LIST' }],
    }),

    // Get single device by ID
    getDevice: builder.query<Device, string>({
      query: (id) => `devices/${id}`,
      transformResponse: (response: any) => transformDevice(response),
      providesTags: (_result, _error, id) => [{ type: 'Device', id }],
    }),

    // Update device
    updateDevice: builder.mutation<ApiResponse<Device>, { id: string; data: UpdateDeviceRequest }>({
      query: ({ id, data }) => ({
        url: `devices/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Device', id },
        { type: 'Device', id: 'LIST' },
      ],
    }),

    // Delete device
    deleteDevice: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `devices/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Device', id },
        { type: 'Device', id: 'LIST' },
      ],
    }),

    bulkDeleteDevices: builder.mutation<BulkArchiveResponse, string[]>({
      query: (ids) => ({
        url: 'devices/bulk/delete',
        method: 'POST',
        body: { device_ids: ids },
      }),
      invalidatesTags: (_result, _error, ids) => [
        ...ids.map((id) => ({ type: 'Device' as const, id })),
        { type: 'Device', id: 'LIST' },
      ],
    }),

    // Get unverified devices
    getUnverifiedDevices: builder.query<Device[], void>({
      query: () => 'devices/unverified',
      transformResponse: (response: any[]) => response.map(transformDevice),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Device' as const, id })),
              { type: 'Device', id: 'UNVERIFIED' },
            ]
          : [{ type: 'Device', id: 'UNVERIFIED' }],
    }),

    // Verify device - using bulk-verify endpoint to bypass status restrictions
    verifyDevice: builder.mutation<BulkVerifyResponse, string>({
      query: (deviceId) => ({
        url: `devices/bulk-verify`,
        method: 'POST',
        body: {
          device_ids: [deviceId],
        },
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Device', id },
        { type: 'Device', id: 'LIST' },
        { type: 'Device', id: 'UNVERIFIED' },
      ],
    }),

    // Reject device
    rejectDevice: builder.mutation<void, { deviceId: string; reason: string }>({
      query: ({ deviceId, reason }) => ({
        url: `devices/${deviceId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { deviceId }) => [
        { type: 'Device', id: deviceId },
        { type: 'Device', id: 'LIST' },
        { type: 'Device', id: 'UNVERIFIED' },
      ],
    }),

    // Change device passwords
    changeDevicePasswords: builder.mutation<{
      message: string;
      command_id: string;
      device_id: string;
      users_affected: string[];
      requested_by: string;
      requested_at: string;
      status: string;
    }, string>({
      query: (deviceId) => ({
        url: `devices/${deviceId}/change-passwords`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, deviceId) => [
        { type: 'Device', id: deviceId },
        { type: 'Device', id: 'LIST' },
      ],
    }),

    // Update device secure credentials (serial and code)
    updateDeviceSecureCredentials: builder.mutation<Device, {
      deviceId: string;
      secure_serial?: number;
      secure_code?: string;
    }>({
      query: ({ deviceId, secure_serial, secure_code }) => ({
        url: `devices/${deviceId}/secure-credentials`,
        method: 'PATCH',
        body: {
          secure_serial,
          secure_code,
        },
      }),
      transformResponse: (response: any) => transformDevice(response),
      invalidatesTags: (_result, _error, { deviceId }) => [
        { type: 'Device', id: deviceId },
        { type: 'Device', id: 'LIST' },
      ],
    }),

    // Check if secure serial is available
    checkSerialAvailability: builder.query<{
      available: boolean;
      message: string;
      used_by_device_id?: string;
    }, { serial: number; excludeDeviceId?: string }>({
      query: ({ serial, excludeDeviceId }) => ({
        url: `devices/check-serial/${serial}`,
        params: excludeDeviceId ? { exclude_device_id: excludeDeviceId } : undefined,
      }),
    }),

    // Check if secure code is available
    checkCodeAvailability: builder.query<{
      available: boolean;
      message: string;
      used_by_device_id?: string;
    }, { code: string; excludeDeviceId?: string }>({
      query: ({ code, excludeDeviceId }) => ({
        url: `devices/check-code/${code}`,
        params: excludeDeviceId ? { exclude_device_id: excludeDeviceId } : undefined,
      }),
    }),

    // Terminate the active terminal SSH session for a device
    terminateTerminalSession: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `/terminal/devices/${deviceId}/terminate-session`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, deviceId) => [{ type: 'Device', id: deviceId }],
    }),

    // Generate device labels in bulk → returns a PDF blob
    generateDeviceLabels: builder.mutation<Blob, { deviceIds: string[]; partNo: string }>({
      query: ({ deviceIds, partNo }) => ({
        url: '/devices/label/generate-bulk',
        method: 'POST',
        body: {
          device_ids: deviceIds,
          part_no: partNo,
        },
        responseHandler: (response) => response.blob(),
      }),
      invalidatesTags: (_result, _error, { deviceIds }) => [
        ...deviceIds.map((id) => ({ type: 'Device' as const, id: `${id}-label` })),
        { type: 'Device', id: 'LIST' },
      ],
    }),

    getDeviceOutages: builder.query<DeviceStatusHistory, { deviceId: string; hours: number }>({
      query: ({ deviceId, hours }) => ({
        url: `/device-status/${deviceId}/history`,
        params: { hours, status: 'offline', limit: 1000 },
      }),
    }),
  }),
});

export const {
  useGetDeviceOutagesQuery,
  useGetDevicesQuery,
  useGetDeviceQuery,
  useUpdateDeviceMutation,
  useDeleteDeviceMutation,
  useBulkDeleteDevicesMutation,
  useGetUnverifiedDevicesQuery,
  useVerifyDeviceMutation,
  useRejectDeviceMutation,
  useChangeDevicePasswordsMutation,
  useUpdateDeviceSecureCredentialsMutation,
  useLazyCheckSerialAvailabilityQuery,
  useLazyCheckCodeAvailabilityQuery,
  useTerminateTerminalSessionMutation,
  useGenerateDeviceLabelsMutation,
} = deviceApi;
